import React, { useMemo } from 'react'
import { useMatchScreen } from '../../context/MatchScreenContext'
import toTitleCase from '../../utils/toTitleCase'

const FIRST_NAME_TOKEN = '[first_name]'

/** Replaces every `[first_name]` token in a string run with an `mp-mask` span. */
const maskNameInText = (text: string, firstName: string): React.ReactNode =>
  text.split(FIRST_NAME_TOKEN).flatMap((segment, i) =>
    i === 0 ? segment : [<span key={i} className="mp-mask">{firstName}</span>, segment]
  )

/**
 * Walks a (possibly highlight-resolved) `ReactNode` and replaces `[first_name]`
 * tokens in every text run with `firstName` wrapped in an `mp-mask` span —
 * recursing into elements so a name nested inside `{highlight}` is both
 * highlighted and masked. Resolve highlights first (e.g. `useHighlightedText`),
 * otherwise the `{highlight}` pair around a name is split across nodes and
 * never resolves.
 */
const withMaskedName = (node: React.ReactNode, firstName: string): React.ReactNode =>
  React.Children.map(node, (child) =>
    typeof child === 'string'
      ? maskNameInText(child, firstName)
      : React.isValidElement(child)
        ? React.cloneElement(child, undefined, withMaskedName(child.props.children, firstName))
        : child
  )

/**
 * Reads the user's first name from match-screen answers and returns a
 * `maskFirstName` transform that replaces `[first_name]` tokens with the name
 * wrapped in an `mp-mask` span. FSM-989: keeps the name out of Mixpanel session
 * replay while surrounding copy stays visible. `maskFirstName` is a no-op when
 * the name is unknown, so the token simply disappears rather than rendering an
 * empty mask. `firstName` is also returned for callers that branch on it (e.g.
 * choosing a generic fallback heading). Must be called inside `MatchScreenProvider`.
 *
 * @example
 * const { firstName, maskFirstName } = useMaskedFirstName()
 * const text = useHighlightedText(firstName ? configured : fallback)
 * return <h1>{maskFirstName(text)}</h1>
 */
interface MaskedFirstName {
  firstName: string
  maskFirstName: (node: React.ReactNode) => React.ReactNode
}

export function useMaskedFirstName (): MaskedFirstName {
  const { state: { storedAnswers } } = useMatchScreen()

  // Formatted at the single read site so the presence gate and the rendered
  // .mp-mask span both see the same value.
  const firstName = toTitleCase(storedAnswers.find(
    (r) => r.questionName === 'first_name'
  )?.answer)

  const maskFirstName = useMemo(
    () => (node: React.ReactNode): React.ReactNode =>
      firstName ? withMaskedName(node, firstName) : node,
    [firstName]
  )

  return { firstName, maskFirstName }
}
